import type { ComponentType } from 'react';

import type { ProductKeys3P as ProductKeys3PType } from '@atlassian/rovo-connectors-config/types';

import {
	SvgSetupTeamUpdateFigma,
	SvgSetupTeamUpdateGdrive,
	SvgSetupTeamUpdateGithub,
	SvgSetupTeamUpdateOnedrive,
	SvgSetupTeamUpdateSlack,
} from '../../../assets/screens/third-party-apps';
import {
	resolveRecommendedProductKeys,
	type SmartlinkConnectorRecommendationInputs,
} from '../../utils/smartlink-connector-recommendations';

/**
 * The subset of third-party apps that have a dedicated animation SVG.
 * Apps not in this list (e.g. `sharepoint`) should not be passed as `topApps` —
 * the caller should pre-filter to only these values before calling `getAnimationSvg`.
 */
export const ANIMATION_APP_KEYS = ['figma', 'github', 'slack', 'google-drive', 'onedrive'] as const;
export type AnimationApp = (typeof ANIMATION_APP_KEYS)[number];

/**
 * Department/team-type keys as produced by `resolveTeamTypeKey` in the Post
 * Office implicit-message-creator. Values use `snake_case` to match the TAP
 * trait key shape and the `HARDCODED_DEFAULTS` lookup key.
 */
export const DEPARTMENT_KEYS = [
	'design',
	'product_management',
	'marketing',
	'engineering',
	'information_technology',
	'other',
	'operations',
	'finance',
	'legal',
	'human_resources',
	'na',
	'program_management',
	'sales',
] as const;
export type Department = (typeof DEPARTMENT_KEYS)[number];

const DEPARTMENTS = new Set<string>(DEPARTMENT_KEYS);
const PRODUCT_KEY_TO_ANIMATION_APP: Partial<Record<ProductKeys3PType, AnimationApp>> = {
	figma: 'figma',
	drive: 'google-drive',
	github: 'github',
	onedrive: 'onedrive',
	slack: 'slack',
};

export const resolveAnimationInputs = ({
	smartlinkConnectorRecommendationInputs,
	teamTypeKey,
}: {
	smartlinkConnectorRecommendationInputs?: SmartlinkConnectorRecommendationInputs;
	teamTypeKey?: string;
}): {
	department?: Department;
	topApps: AnimationApp[];
} => {
	// Animation picks the highest-priority recommended app ONLY when that
	// app comes from real TAP-trait signal (Tier 1 or Tier 2). We use the
	// `traitOnly` option to opt out of Tier 3 HARDCODED_DEFAULTS, because
	// the department-based animation fallback below already encodes the
	// same team-type-curated mapping in a more visually-coherent way (e.g.
	// engineering animates GitHub directly via the department switch even
	// though Tier 3 would also pick GitHub as the engineering default).
	// Letting Tier 3 fire here would always return non-empty results and
	// rob the department branch of ever running for cold-start tenants.
	const firstSupportedAnimationApp = resolveRecommendedProductKeys(
		smartlinkConnectorRecommendationInputs,
		undefined,
		{ traitOnly: true },
	).find((productKey) => PRODUCT_KEY_TO_ANIMATION_APP[productKey]);
	const topApp = firstSupportedAnimationApp
		? PRODUCT_KEY_TO_ANIMATION_APP[firstSupportedAnimationApp]
		: undefined;

	return {
		topApps: topApp ? [topApp] : [],
		department:
			teamTypeKey && DEPARTMENTS.has(teamTypeKey) ? (teamTypeKey as Department) : undefined,
	};
};

function getAnimationSvgForApp(app: AnimationApp): ComponentType {
	switch (app) {
		case 'figma':
			return SvgSetupTeamUpdateFigma;
		case 'github':
			return SvgSetupTeamUpdateGithub;
		case 'slack':
			return SvgSetupTeamUpdateSlack;
		case 'google-drive':
			return SvgSetupTeamUpdateGdrive;
		case 'onedrive':
			return SvgSetupTeamUpdateOnedrive;
	}
}

/**
 * Returns the animation SVG component to display in the SplitScreen based on:
 *
 * 1. If `topApps` is non-empty, returns the SVG for `topApps[0]` (the highest-priority
 *    app). The caller is responsible for pre-filtering to `AnimationApp` values only —
 *    every element in `topApps` is guaranteed to have a dedicated SVG.
 * 2. If `topApps` is empty or not provided, fall back to a
 *    department-based mapping (department values are snake_case team-type keys):
 *    - Figma:        design, product_management, marketing
 *    - GitHub:       engineering, information_technology
 *    - Slack:        other, operations
 *    - Google Drive: finance, legal, human_resources, na, program_management, sales
 */
export function getAnimationSvg({
	topApps,
	department,
}: {
	department?: Department;
	topApps?: AnimationApp[];
}): ComponentType {
	// Step 1: return the SVG for the highest-priority app if any are provided.
	// topApps is pre-filtered to AnimationApp values, so every element has a
	// dedicated SVG — we just take the first one.
	if (topApps && topApps.length > 0) {
		return getAnimationSvgForApp(topApps[0]);
	}

	// Step 2: department-based fallback
	if (department) {
		switch (department) {
			case 'design':
			case 'product_management':
			case 'marketing':
				return SvgSetupTeamUpdateFigma;
			case 'engineering':
			case 'information_technology':
				return SvgSetupTeamUpdateGithub;
			case 'other':
			case 'operations':
				return SvgSetupTeamUpdateSlack;
			case 'finance':
			case 'legal':
			case 'human_resources':
			case 'na':
			case 'program_management':
			case 'sales':
				return SvgSetupTeamUpdateGdrive;
		}
	}

	// Final fallback: Google Drive (matches the original hardcoded SvgTeamUpdate)
	return SvgSetupTeamUpdateGdrive;
}
